// src/components/Customer-Reviews/CustomerReviews.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useReviews, Review } from '../../Hooks/useReviews';

const reviewStyles = `
  /* ── Section wrapper ── */
  .cr-section {
    background: #ffffff;
    padding: 0 0 100px;
    position: relative;
    overflow: visible;
  }

  /* ── Heading ── */
  .cr-heading-label {
    font-size: 11px;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: #717fe0;
    display: block;
    margin-bottom: 12px;
    font-family: Poppins-Medium;
  }

  .cr-heading-title {
    font-family: Poppins-Bold;
    font-size: 36px;
    line-height: 1.15;
    text-transform: uppercase;
    color: #1a1a2e;
    margin: 0 0 8px;
    letter-spacing: 0.02em;
  }

  .cr-heading-sub {
    font-family: Poppins-Light;
    font-size: 15px;
    color: rgba(0,0,0,0.45);
    margin: 0;
  }

  .cr-heading-line {
    width: 48px;
    height: 3px;
    background: #717fe0;
    border-radius: 2px;
    margin: 18px auto 0;
  }

  /* ── Carousel track ── */
  .cr-carousel-outer {
    position: relative;
    overflow: hidden;
    margin: 0 -15px;
    padding: 68px 0 16px;
  }

  .cr-track {
    display: flex;
    transition: transform 0.55s cubic-bezier(0.25, 1, 0.5, 1);
    will-change: transform;
    align-items: stretch;
  }

  /* ── Individual card ── */
  .cr-card {
    flex: 0 0 calc(33.333% - 30px);
    margin: 0 15px;
    background: #f0f0f0;
    border-radius: 16px;
    padding: 72px 28px 32px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    position: relative;
    box-shadow:
      inset 2px 2px 5px rgba(255,255,255,0.75),
      inset -2px -2px 5px rgba(0,0,0,0.10),
      4px 4px 12px rgba(0,0,0,0.10);
    transition: transform 0.35s ease, box-shadow 0.35s ease;
    cursor: default;
    text-align: center;
  }

  .cr-card:hover {
    transform: translateY(-6px);
    box-shadow:
      inset 2px 2px 5px rgba(255,255,255,0.75),
      inset -2px -2px 5px rgba(0,0,0,0.10),
      8px 8px 24px rgba(0,0,0,0.15);
  }

  /* ── Floating avatar container ── */
  .cr-avatar-wrap {
    position: absolute;
    top: -52px;
    left: 50%;
    transform: translateX(-50%);
    width: 104px;
    height: 104px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .cr-avatar-inner {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;
    background: linear-gradient(135deg, #717fe0 0%, #a78bfa 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Poppins-Bold;
    font-size: 28px;
    color: #fff;
    border: 4px solid #f0f0f0;
    box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  }

  .cr-avatar-inner img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  /* ── Stars ── */
  .cr-stars {
    display: flex;
    gap: 6px;
    justify-content: center;
    margin-top: 4px;
  }

  .cr-star {
    font-size: 20px;
    color: rgba(0,0,0,0.12);
    transition: color 0.2s ease;
  }

  .cr-star.filled {
    color: #f9ba48;
  }

  /* ── Author name ── */
  .cr-author-name {
    font-family: Poppins-Bold;
    font-size: 15px;
    color: #1a1a2e;
    line-height: 1.3;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin: 0;
  }

  /* ── Product name tag ── */
  .cr-product-tag {
    display: inline-block;
    font-family: Poppins-Regular;
    font-size: 10px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #717fe0;
    background: rgba(113,127,224,0.10);
    border: 1px solid rgba(113,127,224,0.28);
    border-radius: 30px;
    padding: 4px 14px;
    width: fit-content;
  }

  /* ── Date badge ── */
  .cr-date-badge {
    display: inline-block;
    font-family: Poppins-Regular;
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(0,0,0,0.38);
    margin-top: -4px;
  }

  /* ── Comment text ── */
  .cr-comment {
    font-family: Poppins-Light;
    font-size: 13.5px;
    line-height: 1.85;
    color: rgba(0,0,0,0.55);
    margin: 0;
    background: rgba(0,0,0,0.04);
    border-radius: 10px;
    padding: 14px 16px;
    width: 100%;
    box-sizing: border-box;
  }

  /* ── Navigation dots ── */
  .cr-dots {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 44px;
  }

  .cr-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(113,127,224,0.2);
    border: none;
    padding: 0;
    cursor: pointer;
    transition: background 0.3s ease, transform 0.3s ease, width 0.3s ease;
  }

  .cr-dot.active {
    background: #717fe0;
    width: 28px;
    border-radius: 4px;
    transform: none;
  }

  /* ── Arrow buttons ── */
  .cr-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #ffffff;
    border: 1.5px solid rgba(113,127,224,0.25);
    color: #717fe0;
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 16px rgba(113,127,224,0.12);
    line-height: 1;
  }

  .cr-arrow:hover {
    background: #717fe0;
    border-color: #717fe0;
    color: #fff;
    transform: translateY(-50%) scale(1.08);
    box-shadow: 0 8px 24px rgba(113,127,224,0.3);
  }

  .cr-arrow:disabled {
    opacity: 0.3;
    cursor: default;
    pointer-events: none;
  }

  .cr-arrow-prev { left: -22px; }
  .cr-arrow-next { right: -22px; }

  /* ── Loading skeleton ── */
  @keyframes cr-shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }

  .cr-skeleton {
    flex: 0 0 calc(33.333% - 30px);
    margin: 0 15px;
    background: #f0f0f0;
    border-radius: 16px;
    padding: 72px 28px 32px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    min-height: 300px;
    position: relative;
  }

  .cr-skeleton-circle {
    position: absolute;
    top: -52px;
    left: 50%;
    transform: translateX(-50%);
    width: 104px;
    height: 104px;
    border-radius: 50%;
    background: linear-gradient(90deg, rgba(113,127,224,0.08) 25%, rgba(113,127,224,0.16) 50%, rgba(113,127,224,0.08) 75%);
    background-size: 400px 100%;
    animation: cr-shimmer 1.4s ease infinite;
  }

  .cr-skeleton-line {
    height: 12px;
    border-radius: 6px;
    background: linear-gradient(90deg, rgba(113,127,224,0.06) 25%, rgba(113,127,224,0.12) 50%, rgba(113,127,224,0.06) 75%);
    background-size: 400px 100%;
    animation: cr-shimmer 1.4s ease infinite;
  }

  /* ── Responsive ── */
  @media (max-width: 991px) {
    .cr-card,
    .cr-skeleton {
      flex: 0 0 calc(50% - 30px);
    }
    .cr-arrow-prev { left: -10px; }
    .cr-arrow-next { right: -10px; }
    .cr-heading-title { font-size: 28px; }
  }

  @media (max-width: 575px) {
    .cr-section { padding: 0 0 70px; }
    .cr-card,
    .cr-skeleton {
      flex: 0 0 calc(100% - 30px);
    }
    .cr-heading-title { font-size: 24px; }
    .cr-card { padding: 72px 18px 24px; }
  }
`;

/* ── Helpers ── */

const formatDate = (timestamp: any): string => {
  if (!timestamp) return '';
  const date =
    typeof timestamp.toDate === 'function'
      ? timestamp.toDate()
      : new Date(timestamp.seconds * 1000);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const getInitials = (name: string): string => {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/* ── Star Rating ── */
const StarRating: React.FC<{ rating: number }> = ({ rating }) => (
  <div className="cr-stars" aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map(i => (
      <span key={i} className={`cr-star${i <= rating ? ' filled' : ''}`}>★</span>
    ))}
  </div>
);

/* ── Avatar ── */
const Avatar: React.FC<{ review: Review }> = ({ review }) => {
  const [imgError, setImgError] = useState(false);
  const imageUrl = review?.imageUrl;

  return (
    <div className="cr-avatar-wrap">
      <div className="cr-avatar-inner">
        {imageUrl && !imgError ? (
          <img
            src={imageUrl}
            alt={review.userName ?? ''}
            onError={() => setImgError(true)}

          />
        ) : (
          getInitials(review?.userName ?? '')
        )}
      </div>
    </div>
  );
};

/* ── Single Review Card ── */
const ReviewCard: React.FC<{ review: Review }> = ({ review }) => (
  <div className="cr-card">
    <Avatar review={review} />

    <StarRating rating={review.rating} />

    <p className="cr-author-name">{review.userName ?? 'Anonymous'}</p>

    {review.productName && (
      <span className="cr-product-tag">{review.productName}</span>
    )}

    {review.createdAt && (
      <span className="cr-date-badge">{formatDate(review.createdAt)}</span>
    )}

    <p className="cr-comment">{review.comment}</p>
  </div>
);

/* ── Skeleton Card ── */
const SkeletonCard: React.FC = () => (
  <div className="cr-skeleton">
    <div className="cr-skeleton-circle" />
    <div className="cr-skeleton-line" style={{ width: '120px', marginTop: '8px' }} />
    <div className="cr-skeleton-line" style={{ width: '80px', height: '9px' }} />
    <div className="cr-skeleton-line" style={{ width: '100%', height: '70px', borderRadius: '10px' }} />
    <div className="cr-skeleton-line" style={{ width: '65%' }} />
  </div>
);

/* ── Main Component ── */

const CustomerReviews: React.FC = () => {
  const { data: reviews = [], isLoading } = useReviews();

  const getVisibleCount = () => {
    if (typeof window === 'undefined') return 3;
    if (window.innerWidth <= 575) return 1;
    if (window.innerWidth <= 991) return 2;
    return 3;
  };

  const [visibleCount, setVisibleCount] = useState(getVisibleCount);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const autoPlayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      const newCount = getVisibleCount();
      setVisibleCount(newCount);
      setCurrentIndex(0);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, reviews.length - visibleCount);

  const goTo = useCallback((index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(Math.max(0, Math.min(index, maxIndex)));
    setTimeout(() => setIsAnimating(false), 600);
  }, [isAnimating, maxIndex]);

  const goPrev = () => goTo(currentIndex - 1);
  const goNext = () => goTo(currentIndex + 1);

  useEffect(() => {
    if (reviews.length === 0) return;
    autoPlayRef.current = setTimeout(() => {
      const nextIndex = currentIndex >= maxIndex ? 0 : currentIndex + 1;
      goTo(nextIndex);
    }, 4500);
    return () => {
      if (autoPlayRef.current) clearTimeout(autoPlayRef.current);
    };
  }, [currentIndex, reviews.length, maxIndex, goTo]);

  const cardWidthPercent = 100 / visibleCount;
  const translateX = -(currentIndex * cardWidthPercent);
  const dotCount = maxIndex + 1;

  return (
    <>
      <style>{reviewStyles}</style>

      <section className="cr-section">
        <div className="container">

          {/* ── Heading ── */}
          {/* <div className="p-b-50 txt-center">
            <span className="cr-heading-label">What Our Customers Say</span>
            <h2 className="cr-heading-title">Customer Reviews</h2>
            <p className="cr-heading-sub">Real experiences from our valued customers</p>
            <div className="cr-heading-line" />
          </div> */}

          {/* ── Carousel ── */}
          <div style={{ position: 'relative', padding: '0 30px' }}>

            {!isLoading && reviews.length > visibleCount && (
              <button
                className="cr-arrow cr-arrow-prev"
                onClick={goPrev}
                disabled={currentIndex === 0}
                aria-label="Previous reviews"
              >
                ‹
              </button>
            )}

            <div className="cr-carousel-outer">
              <div
                ref={trackRef}
                className="cr-track"
                style={{ transform: `translateX(${translateX}%)` }}
              >
                {isLoading
                  ? [0, 1, 2].map(i => <SkeletonCard key={i} />)
                  : reviews.map(review => (
                      <ReviewCard key={review.id} review={review} />
                    ))
                }
              </div>
            </div>

            {!isLoading && reviews.length > visibleCount && (
              <button
                className="cr-arrow cr-arrow-next"
                onClick={goNext}
                disabled={currentIndex >= maxIndex}
                aria-label="Next reviews"
              >
                ›
              </button>
            )}
          </div>

          {/* ── Dots ── */}
          {!isLoading && dotCount > 1 && (
            <div className="cr-dots">
              {Array.from({ length: dotCount }).map((_, i) => (
                <button
                  key={i}
                  className={`cr-dot${i === currentIndex ? ' active' : ''}`}
                  onClick={() => goTo(i)}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          )}

        </div>
      </section>
    </>
  );
};

export default CustomerReviews;